import { useEffect, useRef, useState, ChangeEvent, DragEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { Box, Checkbox, Flex, Label, Text, Textarea, useColorMode, Spinner, Button } from 'theme-ui';
import { deleteActionHighlight, editActionHighlight } from '../../../store/actions/action.Highlighters';
import deleteIcon from "../../../Components/assets/images/Icon/delete.svg";
import editIcon from "../../../Components/assets/images/edit.svg";
import { FaChevronDown, FaChevronUp, FaMinus, FaPlus, FaPaperclip, FaTimes, FaFileAlt } from 'react-icons/fa';
import { uploadMedia } from '../../../store/api';
import { uploadIcon } from '../../../helper/uploadType';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { DeletePopModule } from '../../DeletePopModule/DeletePopModule';
import AddCommentAction from './AddCommentAction';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RootState } from '../../../../@types';
import CustomTooltip from '../../CustomTooltip';
import { rolePermissionsHighLighters } from '../../../utils/CustomHandler';
import theme from '../../../theme';
import { useParams, useLocation } from 'react-router-dom';
import { linkifyText } from '../../../utils/textFormat';
import { LuPenLine } from 'react-icons/lu';
import { bucketUrl } from '../../../utils/global';
import { downloadAttachments } from '../../../utils/GenerateQueryString';

export const ActionCard = ({ index, action: actionProp, selectedHighlight, keys, actionObject, actionId }: any) => {

  const [action, setAction] = useState(actionProp);

  useEffect(() => {
    setAction(actionProp);
  }, [actionProp]);

  const { actionHighlights } = useSelector((state: any) => state.actionHighlight)
  const { stageAllTaskView } = useSelector((state: any) => state.project)
  const { auther } = useSelector((state: RootState) => state.auth);
  const is_Task_Worker = auther?.Role?.map(role => role?.RoleName).every(role => role.toLocaleLowerCase() === "task worker")
  const { stageId } = useParams<{ stageId: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const openReply = params.get('openReply') === 'true';

  const dispatch = useDispatch();
  const [chatOpen, setChatOpen] = useState(false);
  const [selected, setSelected] = useState<number>();
  const [updateName, setUpdateName] = useState<string>();
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [modalAttachments, setModalAttachments] = useState<string[]>([]);
  const [isModalUploading, setIsModalUploading] = useState(false);
  const [modalUploadError, setModalUploadError] = useState("");
  const [isDraggingModal, setIsDraggingModal] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);

  const getFullUrl = (urlStr: string) => {
    if (!urlStr) return '';
    if (urlStr.startsWith('http://') || urlStr.startsWith('https://')) return urlStr;
    const cleanBucket = bucketUrl.endsWith('/') ? bucketUrl.slice(0, -1) : bucketUrl;
    const cleanUrl = urlStr.startsWith('/') ? urlStr : `/${urlStr}`;
    return `${cleanBucket}${cleanUrl}`;
  };

  const isImageFile = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  };

  const getFileExt = (filename: string) => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const [selectedModalIndices, setSelectedModalIndices] = useState<number[]>([]);

  useEffect(() => {
    if (isAttachmentsModalOpen) {
      if (modalAttachments.length === 1) {
        setSelectedModalIndices([0]);
      } else {
        setSelectedModalIndices([]);
      }
    }
  }, [isAttachmentsModalOpen, modalAttachments]);

  const handleToggleModalIndex = (idx: number) => {
    setSelectedModalIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleDownloadSelectedFiles = async () => {
    let urlsToDownload: string[] = [];

    if (selectedModalIndices.length > 0) {
      urlsToDownload = selectedModalIndices
        .map((i) => modalAttachments[i])
        .filter(Boolean);
    } else {
      urlsToDownload = modalAttachments.filter(Boolean);
    }

    if (!urlsToDownload || urlsToDownload.length === 0) return;

    setIsDownloadingFiles(true);
    try {
      await downloadAttachments(urlsToDownload);
    } catch (error) {
      // ignore
    } finally {
      setIsDownloadingFiles(false);
    }
  };

  const uploadModalFilesList = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    setIsModalUploading(true);
    setModalUploadError("");
    const uploadedPaths = [...modalAttachments];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'actionAttachments');
        formData.append('uploadType', 'File');

        const response = await uploadMedia(formData);
        if (response?.data?.status && response?.data?.data) {
          uploadedPaths.push(response.data.data);
        } else {
          setModalUploadError('Failed to upload file');
        }
      }
      setModalAttachments(uploadedPaths);
    } catch (err) {
      console.error('File upload error:', err);
      setModalUploadError('An error occurred during file upload');
    } finally {
      setIsModalUploading(false);
    }
  };

  const handleModalFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await uploadModalFilesList(files);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isModalUploading) {
      setIsDraggingModal(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModal(false);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingModal(false);
    if (isModalUploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await uploadModalFilesList(e.dataTransfer.files);
    }
  };

  const removeModalAttachment = (indexToRemove: number) => {
    setModalAttachments(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const [isSavingModal, setIsSavingModal] = useState(false);

  const saveModalAttachments = () => {
    const currentAttachments = action?.attachment || [];
    const isChanged =
      modalAttachments.length !== currentAttachments.length ||
      modalAttachments.some((url, i) => url !== currentAttachments[i]);

    if (isChanged) {
      setIsSavingModal(true);
      const payload = {
        name: action?.name,
        ...actionObject,
        actionType: selectedHighlight?.name,
        data: {
          attachment: modalAttachments,
        }
      };
      dispatch(
        editActionHighlight(payload, actionId || actionHighlights?._id, (res: any) => {
          setIsSavingModal(false);
          if (res?.data?.status) {
            setIsAttachmentsModalOpen(false);
          }
        })
      );
    } else {
      setIsAttachmentsModalOpen(false);
    }
  };

  const [accordionOpen, setAccordionOpen] = useState(false);
  const blockedByTrade = stageAllTaskView?.map((taskView: any) => ({ label: taskView?.name, value: taskView?._id }))
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isSelectingFile = useRef(false);

  useEffect(() => {
    if (chatOpen && openReply && cardRef.current) {
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [chatOpen, openReply]);
  const isStageMatch = stageId === actionHighlights?.stageId;
  const highlighter = ["Revision", "Background Change", "Plan Check"];

  const updateActionHighlight = (objKey: string, updatedValue: boolean) => {
    setAction((prev: any) => ({ ...prev, [objKey]: updatedValue }));
    const payload = {
      name: action?.name,
      blockingTaskId: action?.blockingTaskId,
      ...actionObject,
      actionType: selectedHighlight?.name,

      data: {
        [objKey]: updatedValue,
      }
    }
    dispatch(editActionHighlight(payload, actionId || actionHighlights?._id))
  }
  const editActionHighlightName = () => {
    setAction((prev: any) => ({ ...prev, name: updateName }));
    const payload = {
      name: action?.name,
      ...actionObject,
      data: {
        name: updateName,
      }
    }
    dispatch(editActionHighlight(payload, actionId || actionHighlights?._id))
  }
  const handleAccordionToggle = () => {
    setAccordionOpen(!accordionOpen);
  };
  const handleSave = () => {
    const isNameChanged = updateName !== actionProp?.name;

    if (updateName?.trim() && isNameChanged) {
      editActionHighlightName();
    }
    setIsEditing(false);
  };

  const editContainerRef = useRef<HTMLDivElement>(null);
  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        editContainerRef.current &&
        !editContainerRef.current.contains(event.target as Node) &&
        !isSelectingFile.current
      ) {
        handleSaveRef.current();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  // const handleCommentToggle = () => {
  //   setChatOpen(!chatOpen); // Toggle comment section
  // };
  const deleteCheckAction = () => {
    dispatch(deleteActionHighlight({ id: deleteData }, actionId || actionHighlights?._id))
  }

  useEffect(() => {
    if (action?.name) {
      setUpdateName(action?.name)
    }
  }, [action?.name])

  useEffect(() => {
    if (!action?.name || action?.name.trim().length === 0) {
      setIsEditing(true);
    }
  }, []);

  useEffect(() => {
    if (action?.comment?.length > 0) {
      setAccordionOpen(true)
      setSelected(index);
      if (openReply) {
        setChatOpen(true);
      }
    }
  }, [index, openReply])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px `;
      textareaRef.current.style.marginTop = "20px";
    }
  }, [updateName]);
  const [colorMode] = useColorMode();
  const modalHeader = colorMode === 'light' ? "#f6f6f6" : "#282e42";
  const titleHeader = colorMode === 'light' ? "#0a0a0a" : "#dadada";
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const el = textareaRef.current;
      requestAnimationFrame(() => {
        try {
          el.focus();
          const len = el.value?.length || 0;
          // place cursor at the end
          el.setSelectionRange(len, len);
        } catch (e) {

        }
        // auto-resize to content
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      });
    }
  }, [isEditing]);

  return (
    <div ref={cardRef} className='mt-2'>
      <Box sx={{ display: "grid", paddingLeft: "0.5rem", borderLeft: `2px solid ${selectedHighlight?.color}`, gap: "1rem", color: titleHeader }} >
        <Flex sx={{ gap: "1rem", }}>
          <Flex sx={{
            background: modalHeader,
            paddingLeft: "12px",
            paddingRight: "20px",
            borderRadius: "8px",
            gap: "10px",
            width: "100%",
            alignItems: "center",
            position: "relative",
            pointerEvents: 'auto'
            // pointerEvents: `${action[keys?.first] ? "none" : "auto"}`
          }}>
            {selectedHighlight?.name === "Subtask" &&
              ((is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>Permission Denied</Tooltip>}>

                <Label sx={{ cursor: "not-allowed", width: "auto" }}>
                  <Checkbox
                    name={`${keys?.third}${index}`}
                    id={`${keys?.third}${index}`}
                    checked={action[keys?.third] || false}
                    disabled={true}
                  />
                </Label>
              </OverlayTrigger> :
                <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>Is Reviewed</Tooltip>}>

                  <Label sx={{ cursor: "pointer", width: "auto" }}>
                    <Checkbox
                      name={`${keys?.third}${index}`}
                      id={`${keys?.third}${index}`}
                      checked={action[keys?.third] || false}
                      // disabled={action[keys?.third] || false}
                      value={action[keys?.third] || false}
                      onChange={(e) => updateActionHighlight(keys?.third, e.target.checked)}
                    />
                  </Label>
                </OverlayTrigger>)}
            {keys?.fourth && (
              (is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? (
                <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>Permission Denied</Tooltip>}>
                  <Label sx={{ cursor: "not-allowed", width: "auto" }}>
                    <Checkbox
                      name={`${keys?.fourth}${index}`}
                      id={`${keys?.fourth}${index}`}
                      checked={action[keys?.fourth] || false}
                      disabled
                    />
                  </Label>
                </OverlayTrigger>
              ) : (
                <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>To Client</Tooltip>}>
                  <Label sx={{ cursor: "pointer", width: "auto" }}>
                    <Checkbox
                      name={`${keys?.fourth}${index}`}
                      id={`${keys?.fourth}${index}`}
                      checked={action[keys?.fourth] ? true : false}
                      onChange={(e) => updateActionHighlight(keys?.fourth, e.target.checked)}
                    />
                  </Label>
                </OverlayTrigger>
              )
            )}
            {(is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? (
              // For task workers with restricted highlighters, check if it's isChecked
              keys?.first?.includes("isChecked") ? (
                // Enable isChecked for task workers
                <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>Is Checked</Tooltip>}>
                  <Label sx={{ cursor: "pointer", width: "auto" }}>
                    <Checkbox
                      name={`${keys?.first}${index}`}
                      id={`${keys?.first}${index}`}
                      checked={action[keys?.first] || false}
                      disabled={false} // Enable isChecked for task workers
                      value={action[keys?.first] || false}
                      onChange={(e) => updateActionHighlight(keys?.first, e.target.checked)}
                    />
                  </Label>
                </OverlayTrigger>
              ) : (
                // Disable other checkboxes for task workers
                <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>Permission Denied</Tooltip>}>
                  <Label sx={{ cursor: "not-allowed", width: "auto" }}>
                    <Checkbox
                      name={`${keys?.first}${index}`}
                      id={`${keys?.first}${index}`}
                      checked={action[keys?.first] || false}
                      disabled={true}
                    />
                  </Label>
                </OverlayTrigger>
              )
            ) : (
              // Normal logic for non-task workers
              <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>{keys?.first?.includes("isChecked") ? "Is Checked" : "Is done"}</Tooltip>}>
                <Label sx={{
                  cursor: (!isStageMatch && highlighter?.includes(selectedHighlight?.name))
                    ? "not-allowed"
                    : "pointer",
                  width: "auto"
                }}>
                  <Checkbox
                    name={`${keys?.first}${index}`}
                    id={`${keys?.first}${index}`}
                    checked={action[keys?.first] || false}
                    disabled={(!isStageMatch && highlighter?.includes(selectedHighlight?.name))}
                    value={action[keys?.first] || false}
                    onChange={(e) => updateActionHighlight(keys?.first, e.target.checked)}
                  />
                </Label>
              </OverlayTrigger>
            )}

            {isEditing ? (
              <Box
                ref={editContainerRef}
                sx={{
                  width: '100%',
                  minHeight: '140px',
                  borderRadius: '8px',
                  border: '1px solid #ccc',
                  background: modalHeader,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  padding: '8px',
                  flex: 1,
                }}
              >
                <Textarea
                  ref={textareaRef}
                  disabled={!isStageMatch && highlighter?.includes(selectedHighlight?.name)}
                  sx={{
                    '&:disabled ': {
                      backgroundColor: `transparent`,
                    },
                    width: '100%',
                    flex: 1,
                    minHeight: '60px',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none !important',
                    color: titleHeader,
                    fontSize: '16px',
                    resize: 'vertical',
                    padding: '4px',
                    borderColor: 'transparent !important',
                  }}
                  value={updateName}
                  onChange={(e) => setUpdateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSave();
                    }
                  }}
                  autoFocus
                />

                {null}
              </Box>
            ) : (
              <Box
                sx={{
                  background: "transparent",
                  padding: "8px",
                  margin: "0",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  display: "inline",
                  flex: 1
                }}
              >
                {linkifyText(updateName || "")}
              </Box>
            )}


            <Flex sx={{ marginLeft: "auto", gap: "1rem" }}>
              {/* Conditionally render the edit icon */}
              {!(isStageMatch === false && highlighter?.includes(selectedHighlight?.name)) && (

                (is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? (
                  <CustomTooltip id={`checkListdisabled${index}`} title={is_Task_Worker && "Permission Denied"}>
                    <Label htmlFor={`checkList${index}`} sx={{ cursor: "not-allowed" }}>
                      {colorMode === 'light' ? <img src={editIcon} alt="edit" /> : <LuPenLine style={{ cursor: 'pointer', width: '24px', height: '24px', color: 'white' }} />}
                    </Label>
                  </CustomTooltip>
                ) : (
                  <Label
                    htmlFor={`checkList${index}`}
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setUpdateName(action?.name || '');
                      setIsEditing(true);
                    }}
                  >
                    {colorMode === 'light' ? <img src={editIcon} alt="edit" /> : <LuPenLine style={{ cursor: 'pointer', width: '24px', height: '24px', color: 'white' }} />}
                  </Label>
                )

              )}

              {/* Conditionally render the delete icon */}
              {!(isStageMatch === false && highlighter?.includes(selectedHighlight?.name)) &&
                (
                  (is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-${index}`}>Permission Denied</Tooltip>}
                    >
                      <Text sx={{ cursor: "not-allowed", pointerEvents: "none" }}>
                        <img src={deleteIcon} alt="delete" width={"23px"} />
                      </Text>
                    </OverlayTrigger>
                  ) : (
                    <Text
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setIsDeleteModelOpen(true);
                        setDeleteData(action?._id);
                      }}
                    >
                      <img src={deleteIcon} alt="delete" width={"23px"} />
                    </Text>
                  )

                )}
            </Flex>

            {action[keys?.first] && <Box sx={{
              position: "absolute", inset: 0, height:
                "1px", background: `${theme?.colors?.black}`, top: "50%"
            }}></Box>}
          </Flex>
          {selectedHighlight?.name === "Blocked By Trade" ? (
            <Flex sx={{ alignItems: "center", gap: "1rem" }}>
              <Box sx={{ width: "315px" }}>
                <Select
                  classNamePrefix="an-simple-select"
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary25: '#F7EFE5',
                    },
                  })}
                  isSearchable={false}
                  name='blockedByTrade'
                  options={blockedByTrade || []}
                  defaultValue={blockedByTrade?.find((option: any) => option.value === action.blockingTaskId)}
                  placeholder={"Select the trade"}
                  value={blockedByTrade?.find((option: any) => option.value === action.blockingTaskId)}
                  onChange={(selectedOption: any) => {
                    updateActionHighlight(keys?.second, selectedOption.value)
                  }}
                />
              </Box>

              {/* Attachment Icon Button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  height: "40px",
                  minWidth: "40px",
                  px: (action?.attachment && action.attachment.length > 0) ? "10px" : "0px",
                  flexShrink: 0,
                  borderRadius: (action?.attachment && action.attachment.length > 0) ? "20px" : "50%",
                  background: colorMode === 'light' ? '#F3F5F6' : '#303133',
                  color: '#6366f1',
                  cursor: "pointer",
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'rgba(99, 102, 241, 0.1)'
                  }
                }}
                onClick={() => {
                  setModalAttachments(action?.attachment || []);
                  setIsAttachmentsModalOpen(true);
                }}
                title="Attachments"
              >
                <FaPaperclip size={14} />
                {(action?.attachment && action.attachment.length > 0) && (
                  <Text sx={{ fontSize: '13px', fontWeight: 600 }}>{action.attachment.length}</Text>
                )}
              </Box>
            </Flex>
          ) : (
            <>
              {/* Checkbox Box (Pill) */}
              {(keys?.second && !highlighter?.includes(selectedHighlight?.name)) && (
                <Flex sx={{ border: "1px solid #D3D5D9", padding: "4px", borderRadius: "8px", alignItems: "center", gap: "1rem" }}>
                  <OverlayTrigger placement='auto' overlay={<Tooltip id="button-tooltip-2">{selectedHighlight?.name}</Tooltip>}>
                    <Text sx={{ display: "flex", alignItems: "center" }}>
                      <img src={selectedHighlight?.icon} alt="" width={"23px"} />
                    </Text>
                  </OverlayTrigger>
                  {(is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? (
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${index}`}>Permission Denied</Tooltip>}>
                      <Label sx={{ cursor: "not-allowed", display: "inline-flex", margin: 0 }}>
                        <Checkbox
                          name={`${keys?.second}${index}`}
                          checked={action[keys?.second] || false}
                          value={action[keys?.second]}
                          disabled
                        />
                      </Label>
                    </OverlayTrigger>
                  ) : (
                    <Label sx={{ cursor: "pointer", display: "inline-flex", margin: 0 }}>
                      <Checkbox
                        name={`${keys?.second}${index}`}
                        checked={action[keys?.second] || false}
                        value={action[keys?.second]}
                        onChange={(e) => updateActionHighlight(keys.second, e.target.checked)}
                      />
                    </Label>
                  )}
                </Flex>
              )}

              {/* Vertical Stack for Plus/Minus and Attachment Icon */}
              <Flex sx={{ flexDirection: "column", gap: "8px", alignItems: "center" }}>
                {/* Plus/Minus Toggle */}
                {['Delivery Notes', 'Background Change', 'Plan Check', 'Revision', 'Subtask']?.indexOf(selectedHighlight?.name) === -1 && (
                  <Box
                    sx={{
                      display: "grid",
                      placeContent: "center",
                      width: "45px",
                      height: "40px",
                      borderRadius: "50%",
                      background: colorMode === 'light' ? '#F3F5F6' : '#303133',
                      cursor: "pointer",
                      '&:hover': {
                        background: colorMode === 'light' ? '#E2E4E6' : '#404144',
                      }
                    }}
                    onClick={() => {
                      setChatOpen(!chatOpen);
                      setSelected(index);
                    }}
                  >
                    {chatOpen && selected === index ? <FaMinus /> : <FaPlus />}
                  </Box>
                )}

                {/* Attachment Icon Button */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    height: "40px",
                    minWidth: "40px",
                    px: (action?.attachment && action.attachment.length > 0) ? "10px" : "0px",
                    flexShrink: 0,
                    borderRadius: (action?.attachment && action.attachment.length > 0) ? "20px" : "50%",
                    background: colorMode === 'light' ? '#F3F5F6' : '#303133',
                    color: '#6366f1',
                    cursor: "pointer",
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(99, 102, 241, 0.1)'
                    }
                  }}
                  onClick={() => {
                    setModalAttachments(action?.attachment || []);
                    setIsAttachmentsModalOpen(true);
                  }}
                  title="Attachments"
                >
                  <FaPaperclip size={14} />
                  {(action?.attachment && action.attachment.length > 0) && (
                    <Text sx={{ fontSize: '13px', fontWeight: 600 }}>{action.attachment.length}</Text>
                  )}
                </Box>
              </Flex>
            </>
          )}

        </Flex >
        {chatOpen && selected === index ? <AddCommentAction actionId={actionId || actionHighlights?._id} nameDec={action?.name} keys={keys} commnets={action?.comment} actionObject={actionObject} level={'clientRequirement'} highlightName={selectedHighlight?.name} colorMode={colorMode} /> : null}

        <DeletePopModule setIsDeleteModelOpen={setIsDeleteModelOpen} updatedDataFun={deleteCheckAction} isDeleteModelOpen={isDeleteModelOpen} message={`Are you sure you want to delete this ${selectedHighlight?.name}`} />

      </Box >
      {selectedHighlight?.name === "Need Info" && (
        <Flex sx={{ justifyContent: 'flex-end', background: colorMode === 'light' ? '#f6f6f6' : '#282e42', width: '85%', borderTop: `2px solid ${selectedHighlight?.color}`, paddingLeft: "0.5rem", marginLeft: '0.5rem', borderRadius: '8px' }} className='mb-3 '>
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '0.3rem',
              backgroundColor: colorMode === 'light' ? '#F3F5F6' : '#282e42',
              borderRadius: '8px',
              justifyContent: 'space-between',
            }}
            onClick={handleAccordionToggle}
          >
            {/* {accordionOpen ? <FaMinus /> : <FaPlus />} */}
            <Text sx={{ fontWeight: 'bold', fontSize: '14px', color: titleHeader }}>
              PM's Response
            </Text>
            <Text sx={{ marginLeft: '0.5rem', }}>{accordionOpen ? <FaChevronUp style={{ color: titleHeader }} /> : <FaChevronDown style={{ color: titleHeader }} />}</Text>
          </Box>
        </Flex>
      )}
      {accordionOpen && (
        <Box sx={{ marginTop: '1rem', padding: '1rem', border: '1px solid #E0E0E0', borderRadius: '8px' }}>
          <AddCommentAction actionId={actionId || actionHighlights?._id} nameDec={action.name} commnets={action.comment} keys={keys} actionObject={actionObject} level={'managerReply'} highlightName={selectedHighlight?.name} colorMode={colorMode} />
        </Box>
      )}

      {/* Attachments Management Modal */}
      <Modal isOpen={isAttachmentsModalOpen} toggle={() => !isModalUploading && setIsAttachmentsModalOpen(false)} centered size="md">
        <ModalHeader toggle={() => !isModalUploading && setIsAttachmentsModalOpen(false)} style={{ backgroundColor: modalHeader, color: titleHeader }}>
          Manage Attachments
        </ModalHeader>
        <ModalBody style={{ backgroundColor: modalHeader, color: titleHeader, padding: '24px' }}>
          {(() => {
            const currentSavedAttachments = action?.attachment || [];
            const hasUnsavedModalChanges =
              modalAttachments.length !== currentSavedAttachments.length ||
              modalAttachments.some((url, i) => url !== currentSavedAttachments[i]);

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Current files list */}
                {modalAttachments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text sx={{ fontWeight: 600, fontSize: '14px', color: titleHeader }}>Current Attachments:</Text>
                      {!isModalUploading && !hasUnsavedModalChanges && (action?.attachment?.length || 0) > 0 && selectedModalIndices.length > 0 && (
                        <Button
                          type="button"
                          disabled={isDownloadingFiles}
                          onClick={handleDownloadSelectedFiles}
                          sx={{
                            fontSize: '12px',
                            padding: '4px 10px',
                            backgroundColor: '#7743DB',
                            color: '#ffffff',
                            borderRadius: '4px',
                            cursor: isDownloadingFiles ? 'not-allowed' : 'pointer',
                            border: 'none',
                            opacity: isDownloadingFiles ? 0.7 : 1,
                          }}
                        >
                          {isDownloadingFiles
                            ? 'Downloading...'
                            : selectedModalIndices.length === 1
                              ? 'Download'
                              : 'Download All'}
                        </Button>
                      )}
                    </Flex>
                    {!isModalUploading && (action?.attachment?.length || 0) > 0 && (selectedModalIndices.length === 0 || hasUnsavedModalChanges) && (
                      <Text sx={{ fontSize: '13px', color: '#ef4444', fontStyle: 'italic', marginTop: '-4px' }}>
                        {hasUnsavedModalChanges
                          ? '* Please save changes to download attachments'
                          : '* Please select checkbox to download attachments'}
                      </Text>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        maxHeight: '240px',
                        overflowY: 'auto',
                        padding: '2px',
                      }}
                    >
                      {modalAttachments.map((url, idx) => {
                        const parts = url.split('/');
                        const filename = parts[parts.length - 1];
                        const fullUrl = getFullUrl(url);
                        const isImg = isImageFile(filename);
                        const ext = getFileExt(filename);
                        const iconSrc = uploadIcon(ext);
                        const savedAttachments = action?.attachment || [];
                        const isSaved = savedAttachments.includes(url);

                        return (
                          <Flex
                            key={idx}
                            sx={{
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: colorMode === 'light' ? '1px solid #E2E8F0' : '1px solid #374151',
                              backgroundColor: colorMode === 'light' ? 'rgba(248, 249, 250, 1)' : 'rgba(13, 202, 240, 0.12)',
                              gap: '12px',
                            }}
                          >
                            <Flex sx={{ alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                              {isSaved && !hasUnsavedModalChanges && (
                                <input
                                  type="checkbox"
                                  checked={selectedModalIndices.includes(idx)}
                                  onChange={() => handleToggleModalIndex(idx)}
                                  style={{ cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px', accentColor: '#7743DB' }}
                                />
                              )}
                              {/* File type icon (jpg, png, pdf, xls, etc.) */}
                              {iconSrc ? (
                                <img
                                  src={iconSrc}
                                  alt={ext}
                                  style={{
                                    width: '22px',
                                    height: '22px',
                                    objectFit: 'contain',
                                    flexShrink: 0,
                                  }}
                                />
                              ) : (
                                <FaFileAlt size={20} color="#0dcaf0" style={{ flexShrink: 0 }} />
                              )}

                              {/* Filename label - non-clickable, shown as Link 1, Link 2 etc. */}
                              <span
                                title={filename}
                                style={{
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  color: '#0dcaf0',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '250px',
                                  display: 'inline-block',
                                  userSelect: 'none',
                                }}
                              >
                                {`Link ${idx + 1}`}
                              </span>
                            </Flex>

                            {/* Remove button */}
                            <FaTimes
                              style={{
                                cursor: isModalUploading ? 'not-allowed' : 'pointer',
                                color: '#ff4d4f',
                                opacity: isModalUploading ? 0.5 : 0.85,
                                flexShrink: 0,
                              }}
                              onClick={() => !isModalUploading && removeModalAttachment(idx)}
                              title="Remove file"
                            />
                          </Flex>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Text sx={{ fontSize: '14px', color: '#8F9195' }}>No attachments uploaded yet.</Text>
                )}

                {/* Error Message */}
                {modalUploadError && (
                  <Text sx={{ color: 'red', fontSize: '13px' }}>
                    {modalUploadError}
                  </Text>
                )}

                {/* Upload Area with Drag & Drop */}
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => {
                    if (!isModalUploading) {
                      document.getElementById(`modal-subtask-attachment-upload-${index}`)?.click();
                    }
                  }}
                  variant={isDraggingModal ? 'cards.uploadDropzoneActive' : 'cards.uploadDropzone'}
                  sx={{
                    cursor: isModalUploading ? 'not-allowed' : 'pointer',
                    border: isDraggingModal
                      ? '2px dashed #0dcaf0'
                      : (colorMode === 'light' ? '2px dashed #cbd5e1' : '2px dashed rgba(255, 255, 255, 0.2)'),
                    backgroundColor: colorMode === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.02)',
                  }}
                >
                  <input
                    type="file"
                    id={`modal-subtask-attachment-upload-${index}`}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleModalFileChange}
                    disabled={isModalUploading}
                  />
                  <Label
                    htmlFor={`modal-subtask-attachment-upload-${index}`}
                    variant="forms.UploadLabel"
                    sx={{
                      cursor: isModalUploading ? 'not-allowed' : 'pointer',
                      pointerEvents: 'none',
                    }}
                  >
                    {isModalUploading ? <Spinner size={16} /> : <FaPaperclip size={14} />}
                    {isModalUploading ? 'Uploading...' : 'Browse Files'}
                  </Label>
                  <Text variant="UploadHint">
                    or drag and drop files here
                  </Text>
                </Box>

                {/* Action Buttons */}
                <Flex sx={{ justifyContent: 'flex-end', gap: '12px', mt: '24px', pt: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <Button
                    type="button"
                    onClick={() => !isModalUploading && setIsAttachmentsModalOpen(false)}
                    disabled={isModalUploading}
                    sx={{
                      background: 'transparent',
                      border: colorMode === 'light' ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.1)',
                      color: colorMode === 'light' ? '#0f172a' : '#f8fafc',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      cursor: isModalUploading ? 'not-allowed' : 'pointer',
                      opacity: isModalUploading ? 0.6 : 1,
                    }}
                  >
                    Cancel
                  </Button>
                  {!isModalUploading && hasUnsavedModalChanges && (
                    <Button
                      type="button"
                      onClick={saveModalAttachments}
                      disabled={isModalUploading || isSavingModal}
                      sx={{
                        background: '#7743DB',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        cursor: (isModalUploading || isSavingModal) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {isSavingModal && <Spinner size={14} color="white" />}
                      {isSavingModal ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                </Flex>
              </Box>
            );
          })()}
        </ModalBody>
      </Modal>
    </div>
  )
}
